import { navigatorClient } from "@/api";
import type { ApiResponse } from "@/types";

// F06 회원가입 (backend /auth/signup · /auth/signup/* · /terms) — 설계서 Front v0.5 slide 88~97
export type SignupUserType = "demand" | "supplier";

// 약관 4종: 이용약관 · 개인정보 처리방침 · 개인정보 수집 및 이용 동의서 (필수) · 개인정보 제3자 제공 동의서 (선택)
export const SIGNUP_TERMS = ["service_terms", "privacy_policy", "privacy_signup", "supplier_contact_share"] as const;
// 제3자 제공 동의는 선택 (동의서 2항: 거부해도 가입 가능). 동의하지 않으면 도입 · 컨설팅 문의 때 다시 물어봐요
export const OPTIONAL_SIGNUP_TERMS: readonly string[] = ["supplier_contact_share"];
export const REQUIRED_SIGNUP_TERMS = SIGNUP_TERMS.filter((code) => !OPTIONAL_SIGNUP_TERMS.includes(code));

// 기업 소재국 (slide 89), 기본값 한국
export const COMPANY_COUNTRIES = ["한국", "미국", "일본", "중국", "유럽", "기타"] as const;

export interface SignupPayload {
  user_type: SignupUserType;
  company_name: string;
  ceo_name?: string | null; // 수요자
  company_country?: string | null; // 수요자
  department?: string | null; // 수요자
  position?: string | null; // 수요자 (직급)
  requester_name: string;
  phone: string;
  email: string;
  business_reg_no?: string; // 공급자
  business_license_doc_id?: string | null; // 공급자 (사업자등록증 사본 업로드 결과)
  verification_token: string;
  consents: string[];
}

export interface SignupResult {
  id: string;
  email: string;
  user_type: string;
  status: string;
}

export interface TermsItem {
  code: string;
  version: string;
  title: string;
  body: string;
  is_required: boolean;
}

export type EmailVerifyResult = "registered_supplier" | "already_registered" | "invited";

export interface EmailVerifyResponse {
  result: EmailVerifyResult;
  verification_token: string | null;
  // 초대 메일이 실제로 나갔는지 — Graph 초대가 꺼져 있는 환경(권한 승인 전)에서는 false
  mail_sent?: boolean;
}

export interface InviteLookup {
  state: "valid" | "expired";
  matched: boolean;
  prefill: { company_name: string; requester_name: string; phone: string; email: string } | null;
}

export interface InviteCompletePayload {
  token: string;
  entra_access_token: string;
  company_name: string;
  ceo_name: string;
  company_country: string;
  requester_name: string;
  phone: string;
  consents: string[];
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export async function signup(payload: SignupPayload): Promise<SignupResult> {
  // 409 conflict codes are rendered by the screen as popups, not as the global error page
  const { data } = await navigatorClient.post<ApiResponse<SignupResult>>("/auth/signup", payload);
  return data.data!;
}

// 이메일 [인증] (slide 92): 등록 공급사 · 기가입 · 초대 메일 발송
export async function verifyEmail(user_type: SignupUserType, email: string): Promise<EmailVerifyResponse> {
  const { data } = await navigatorClient.post<ApiResponse<EmailVerifyResponse>>("/auth/signup/verify-email", { user_type, email });
  return data.data!;
}

// 공급자 사업자등록증 사본(PDF, 10MB 이하)
export async function uploadBusinessLicense(file: File): Promise<{ document_id: string; filename: string; size: number }> {
  const form = new FormData();
  form.append("file", file);
  // 기본 Content-Type(JSON)이 남아 있으면 axios가 FormData를 JSON으로 바꿔 보내서 파일이 사라져요 → multipart로 명시(경계값은 axios가 붙임)
  const { data } = await navigatorClient.post<ApiResponse<{ document_id: string; filename: string; size: number }>>("/auth/signup/business-license", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.data!;
}

export async function lookupInvite(token: string, entraAccessToken?: string | null): Promise<InviteLookup> {
  const { data } = await navigatorClient.post<ApiResponse<InviteLookup>>("/auth/signup/invite/lookup", {
    token,
    entra_access_token: entraAccessToken || null,
  });
  return data.data!;
}

export async function completeInviteSignup(payload: InviteCompletePayload): Promise<TokenPair> {
  const { data } = await navigatorClient.post<ApiResponse<TokenPair>>("/auth/signup/invite/complete", payload);
  return data.data!;
}

export async function getTerms(codes: string[], signal?: AbortSignal): Promise<TermsItem[]> {
  const { data } = await navigatorClient.get<ApiResponse<TermsItem[]>>("/terms", { params: { codes: codes.join(",") }, signal });
  return data.data ?? [];
}
